/**
 * Call-logs (NOT-15) types. The calling service is the system of record; we
 * proxy its `/api/company/call-logs` API through operations at
 * `/api/voice/call-logs`. Shapes mirror the calling-service contract
 * (docs/calling-service-integration-requirements.md §5.7).
 */

export type CallDirection = 'inbound' | 'outbound';

export interface CallLogItem {
  callControlId: string;
  direction: CallDirection;
  /** Caller E.164 (inbound) / our DID (outbound). */
  from: string;
  /** Callee E.164. */
  to: string;
  /** Resolved contact display name, if the CS matched one. */
  customerName?: string | null;
  /**
   * Calling-service call status — a fixed label set the CS stores and filters
   * on: "Answered" | "Missed" | "Rejected" | "Ringing" | "Voicemail" |
   * "Transferred" | "AI Handled" (NOT Twilio-style "completed"/"no-answer").
   * `GET /call-logs?callStatus=` is validated against exactly these values.
   */
  callStatus?: string | null;
  isMissedCall?: boolean;
  /** Talk time in seconds. */
  duration?: number | null;
  /**
   * Who took the call, by name. Resolved in operations: for a notifi-2.0 call
   * the calling service stores only the agent's id, and the name lives here.
   */
  answeredByName?: string | null;
  /** Name the calling service stored itself (legacy rows). */
  teamMemberName?: string | null;
  /** Legacy name field — on some write paths it holds an agent id instead. */
  pickedBy?: string | null;
  /** When the call itself started/ended (Telnyx), distinct from the record's
   *  `createdAt`. Used to match a live call to its log by time. */
  callStartTime?: string | null;
  callEndTime?: string | null;
  /** ISO timestamp of the call. */
  createdAt?: string | null;
  metadata?: Record<string, unknown> | null;
  /** Present on list rows too when the calling service has a recording. */
  recordingUrl?: string | null;
}

export interface CallTranscriptionLine {
  text: string;
  transcription_track?: string;
}

export type CallSentiment = 'Positive' | 'Neutral' | 'Negative';
export type CallResolution = 'Resolved' | 'Unresolved' | 'Escalated';

/** AI-generated call insight (calling service `callSummary`). */
export interface CallSummary {
  callType?: string | null;
  customerNeed?: string | null;
  overview?: string | null;
  actionItems?: string[] | null;
  followUpAction?: string | null;
  sentiment?: CallSentiment | null;
  resolution?: CallResolution | null;
  keyTopics?: string[] | null;
  importantDetails?: string[] | null;
  generatedAt?: string | null;
  updatedAt?: string | null;
}

/**
 * Telnyx call-quality stats (per leg) the CS folds into metadata. Telnyx sends
 * every value as a string, so these are `number | string` and must be coerced.
 */
export interface CallQualityLeg {
  mos?: number | string;
  jitter_max_variance?: number | string;
  skip_packet_count?: number | string;
  packet_count?: number | string;
}

export interface CallLogDetail extends CallLogItem {
  recordingUrl?: string | null;
  transcription?: CallTranscriptionLine[] | null;
  /**
   * The plain-text transcript the AI summary reads. On-demand transcription
   * saves it here (not into `transcription[]`), and the summary prefers it, so
   * this is the reliable "does a transcript exist" signal — not the array.
   */
  transcriptionSummary?: string | null;
  /** AI insight, present once generated. */
  callSummary?: CallSummary | null;
  /** Team member who answered (older CS versions). */
  answerBy?: string | null;
  /** When a person (not the IVR) picked up. Absent on unanswered calls and
   *  on rows written before the calling service recorded it. */
  answeredAt?: string | null;
  metadata?:
    | (Record<string, unknown> & {
        endedBy?: 'caller' | 'agent';
        /** The user whose voicemail step took this message (a user id). */
        voicemailFor?: string;
        call_quality_stats?: {
          inbound?: CallQualityLeg;
          outbound?: CallQualityLeg;
        };
      })
    | null;
}

/** Response of POST /call-logs/summary — the generated/saved insight. */
export interface CallSummaryResponse {
  success: boolean;
  data?: { callSummary?: CallSummary } | null;
  message?: string;
}

/** Response of POST /call-logs/transcribe. */
export interface CallTranscribeResponse {
  success: boolean;
  data?: { transcription?: CallTranscriptionLine[] | string } | null;
  message?: string;
}

// ─── Report / analytics ──────────────────────────────────────────────────────

export type ReportInterval =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last30days'
  | 'thisMonth'
  | 'lastMonth'
  | 'custom';

/**
 * ServiceTitan's inbound call classification. Everything except `booked` /
 * `unbooked` comes from the call itself; those two are merged in by
 * operations, which is the only side that can see whether an appointment came
 * out of the call. They read null on a deployment where that join is
 * unavailable — which is not the same as "nothing was booked".
 */
export interface CallClassification {
  inbound: number;
  answered: number;
  /** Answered inbound calls over the 60s line — a real chance to book. */
  leadOpportunities: number;
  /** Answered but too short to have been a bookable conversation. */
  excused: number;
  /** Inbound calls nobody ever picked up (voicemail counted separately). */
  abandoned: number;
  voicemail: number;
  aiHandled: number;
  booked: number | null;
  unbooked: number | null;
  /**
   * Work that came from a call we cannot place in time — no interaction fact
   * recorded when it was handled, so it is matched by the booking's own clock.
   *
   * Reported BESIDE `booked`, never inside it: around a period edge one of
   * these may belong to the neighbouring period, and `booked` / `bookingRate`
   * have to stand on their own. Real bookings all the same, so show them —
   * a tenant whose calls never record a pickup would otherwise read zero.
   * Absent on a deployment that does not report it.
   */
  bookedApproximate?: number | null;
}

/** Percentages, 0–100 to one decimal. Null means the denominator was zero —
 *  render that as "—", never as 0%. */
export interface CallRates {
  answerRate: number | null;
  abandonmentRate: number | null;
  transferRate: number | null;
  bookingRate: number | null;
}

/**
 * Seconds. Measured from the pickup, so this is conversation rather than time
 * on the line. Calls logged before the service stored a pickup timestamp fall
 * back to their full duration, which includes ring time.
 */
export interface CallHandling {
  answeredCalls: number;
  totalTalkTime: number;
  avgTalkTime: number | null;
  /** How long callers waited before someone picked up. Null for calls with no
   *  recorded pickup. */
  avgSpeedToAnswer: number | null;
}

export interface CallReportDay {
  date: string;
  count: number;
  inbound: number;
  outbound: number;
  answered: number;
  abandoned: number;
  /** Answered inbound calls over the 60s line — the day's own denominator. */
  leadOpportunities: number;
  booked: number | null;
  /** 0–100. Null when the day had no lead calls, which is not the same as a
   *  day that booked none of them. */
  bookingRate: number | null;
}

/** Why a call is on the unbooked list. */
export type UnbookedOutcome = 'unbooked' | 'abandoned';

export interface UnbookedCall extends CallLogItem {
  outcome: UnbookedOutcome;
  recordingUrl?: string | null;
}

export interface UnbookedCallsResponse {
  success: boolean;
  data: {
    calls: UnbookedCall[];
    /** Proven, not inferred — there is genuinely another row. */
    hasMore: boolean;
  };
}

export interface UnbookedCallsParams {
  startDate?: string;
  endDate?: string;
  limit?: number;
}

/** One cell of the volume heatmap. `weekday` is ISO: 1 = Monday … 7 = Sunday. */
export interface CallReportHour {
  weekday: number;
  hour: number;
  count: number;
  answered: number;
}

/** A CSR's scorecard row. */
export interface CallReportMember {
  userId: string;
  name: string | null;
  total: number;
  incoming: number;
  outgoing: number;
  /** Reached this agent — transfers and AI-handled included, since a
   *  transferred call was picked up before it was passed on. Same definition
   *  the whole report uses, and the population talkTime and leadOpportunities
   *  are counted over. */
  answered: number;
  missed: number;
  transferred: number;
  /** Seconds. */
  talkTime: number;
  avgTalkTime: number | null;
  leadOpportunities: number;
  booked: number | null;
  bookingRate: number | null;
  avgDuration: number | null;
}

/**
 * Who was on the other end, over EVERY inbound call.
 *
 * `known` / `unknown` come from the caller-id match the call itself recorded,
 * so unlike `byCallType` this is the whole period rather than the handful of
 * calls somebody generated an AI insight on. The two conversion counts are
 * filled by operations, which holds the contacts — they read null on a
 * deployment where that lookup is unavailable, which is not the same as "no
 * stranger ever became a customer".
 */
export interface CallerMix {
  /**
   * DISTINCT CALLERS, not calls — a number that rang nine times is one person,
   * because the conversion counts below are per person too and the buckets have
   * to reconcile. Callers with no number at all (withheld, some SIP trunks) are
   * in neither, so these need not sum to inbound calls.
   */
  known: number;
  /** Callers whose number matched nobody when they rang. */
  unknown: number;
  /** Strangers who have a contact now, created at or after their call. */
  becameContact: number | null;
  /** Strangers whose number still belongs to nobody. */
  neverContact: number | null;
  /**
   * How many strangers were actually classified — always the two above summed,
   * and NOT necessarily `unknown`. The list operations resolves is capped
   * upstream and unusable numbers are dropped, so on a busy period this is a
   * strict subset and the difference is uncounted, not zero.
   */
  resolved?: number | null;
}

/**
 * The Action Needed report, as one answer.
 *
 * Two clocks, kept apart in the shape itself so a component cannot mix them
 * up: `period` is the cohort of calls that came IN during the range, and
 * `backlog` / `aging` are asked of NOW and ignore the range entirely.
 */
export interface ActionNeededReportData {
  /** What the "as of" line reads, so the page never guesses how stale it is. */
  computedAt: string;
  timezone: string | null;
  period: {
    total: number;
    resolved: number;
    open: number;
    /** Null when nothing came in — render "—", never 0%. */
    resolutionRate: number | null;
    firstResponse: ReportDuration;
    timeToClose: ReportDuration;
  };
  backlog: {
    open: number;
    overdue: number;
    cold: number;
    /** Cuts ACROSS the other three rather than adding to them. */
    unassigned: number;
    oldest: { callAt: string; phone: string } | null;
  };
  aging: { bucket: string; count: number }[];
  byReason: { reason: CallFollowUpReason; total: number; resolved: number }[];
  byStatus: { status: string; count: number }[];
  byOutcome: {
    outcomeId: string | null;
    label: string | null;
    polarity: CallOutcomePolarity | null;
    count: number;
  }[];
  byOwner: {
    userId: string | null;
    name: string | null;
    assigned: number;
    resolved: number;
    resolutionRate: number | null;
  }[];
  byDay: { date: string; created: number; resolved: number }[];
  byHour: { hour: number; count: number }[];
  /**
   * Every weekday-and-hour cell, Sunday first, dense — 168 of them, zeros
   * included. The grid draws an hour nobody rang as a zero rather than a gap,
   * and `byHour` is this summed down the columns, so the two cannot disagree.
   */
  byWeekdayHour: { weekday: number; hour: number; count: number }[];
  worstWindows: { weekday: string; hour: number; count: number }[];
  /** DISTINCT callers, not calls — one number that rang nine times is one person. */
  callers: {
    distinct: number;
    known: number;
    unknown: number;
    repeat: number;
    top: {
      phoneKey: string;
      phone: string;
      calls: number;
      known: boolean;
      /** Who the number belongs to, when a contact carries it. */
      name: string | null;
      /** Sent with the name so the row can open them, never one without the other. */
      contactId: string | null;
    }[];
  };
  /** The equal-length window before this one. Null when no range was given. */
  previous: {
    from: string;
    to: string;
    total: number;
    resolved: number;
    resolutionRate: number | null;
  } | null;
}

/**
 * Median and p90, with the denominator they were measured over.
 *
 * `countedOver` is NOT `outOf`: a call nobody ever rang back has no response
 * time and is excluded rather than counted as zero, so the card has to be able
 * to say "over 318 of 412" instead of implying it measured everything.
 */
export interface ReportDuration {
  medianSeconds: number | null;
  p90Seconds: number | null;
  countedOver: number;
  outOf: number;
}

export interface ActionNeededReportResponse {
  success: boolean;
  data: ActionNeededReportData;
}

/** Every field is one the list already takes, and means the same thing. */
export interface ActionNeededReportParams {
  reason?: CallFollowUpReason;
  owner?: CallFollowUpOwnerFilter;
  assignedToUserId?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  outcomeId?: string;
  polarity?: CallOutcomePolarity;
  unfiled?: boolean;
}

export interface CallReportData {
  period?: { startDate: string; endDate: string; timezone?: string };
  calls?: { total: number; incoming: number; outgoing: number };
  duration?: { avg: number; min?: number; max?: number };
  byDay?: CallReportDay[];
  byHour?: CallReportHour[];
  byStatus?: Record<string, number>;
  bySentiment?: Record<string, number>;
  byResolution?: Record<string, number>;
  byCallType?: Record<string, number>;
  classification?: CallClassification;
  rates?: CallRates;
  handling?: CallHandling;
  byMember?: CallReportMember[];
  insights?: { withTranscript: number; withAISummary: number };
  callers?: CallerMix;
  /** More strangers rang than the report resolved, so `becameContact` is a floor. */
  unknownCallersTruncated?: boolean;
}

export interface CallReportResponse {
  success: boolean;
  data: CallReportData;
}

export interface CallReportParams {
  interval?: ReportInterval;
  startDate?: string;
  endDate?: string;
}

/**
 * The calling service's own pagination block. It names the current page
 * `currentPage` and the row count `totalCount` — not `page` / `total`, which is
 * what this used to say and why the footer only ever counted the rows it had
 * been handed.
 */
export interface CallLogsPagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
}

/** Envelope the calling service returns: `{ success, data: { callLogs, pagination } }`. */
export interface CallLogsResponse {
  success: boolean;
  data: {
    callLogs: CallLogItem[];
    pagination: CallLogsPagination;
  };
}

export interface CallLogDetailResponse {
  success: boolean;
  data: CallLogDetail;
}

export interface ListCallLogsParams {
  page?: number;
  limit?: number;
  search?: string;
  direction?: CallDirection;
  /** A CallLog status label ("Answered" | "Missed" | … ), not a Twilio-style
   *  provider status. See `CallLogSummary.callStatus`. */
  callStatus?: string;
  isMissedCall?: boolean;
  startDate?: string;
  endDate?: string;
}

// ─── Action Needed (call follow-ups) ─────────────────────────────────────────

/**
 * Unlike the rest of this file, these are NOT the calling service's shapes.
 * Follow-up state lives in operations' own Postgres — the calling service owns
 * the call, but only we know whether a human has acted on it.
 */
export type CallFollowUpTab = 'open' | 'done';

export type CallFollowUpReason = 'ABANDONED' | 'VOICEMAIL' | 'UNBOOKED_LEAD';

export type CallFollowUpDisposition =
  | 'CALLED_BACK'
  | 'TEXTED_BACK'
  | 'PROMOTED_TO_LEAD'
  | 'NOT_A_LEAD'
  | 'SPAM'
  | 'WRONG_NUMBER'
  | 'ALREADY_HANDLED'
  | 'AUTO_RESOLVED_CALLBACK'
  | 'AUTO_RESOLVED_BOOKED';

/** The dispositions a person may choose. `AUTO_*` are the system's alone. */
export const MANUAL_DISPOSITIONS = [
  'CALLED_BACK',
  'TEXTED_BACK',
  'NOT_A_LEAD',
  'SPAM',
  'WRONG_NUMBER',
  'ALREADY_HANDLED',
] as const satisfies readonly CallFollowUpDisposition[];

/**
 * The colour a tenant files an outcome under. Three, not two — most real calls
 * land in the middle (a message left, a callback booked for Tuesday), and
 * forcing those into "negative" makes a won/lost report unreadable.
 */
export type CallOutcomePolarity = 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';

/**
 * One entry in the tenant's own outcome vocabulary.
 *
 * Tenant rows rather than an enum because the list is theirs to write. Each
 * one carries the `CallFollowUpDisposition` it stamps, which is what lets the
 * vocabulary vary while the queue's own state machine stays fixed.
 */
export interface CallOutcome {
  id: string;
  label: string;
  /** The identity a rename cannot move. Iris's suggestion resolves by it. */
  slug: string;
  polarity: CallOutcomePolarity;
  disposition: CallFollowUpDisposition;
  /** Seeded by us. Still renameable — the slug is what code holds. */
  isSystem: boolean;
  /** Archived outcomes stay on history but leave the picker. */
  isActive: boolean;
  sortOrder: number;
}

export interface CallOutcomesResponse {
  success: boolean;
  data?: CallOutcome[];
}

export interface CallOutcomeResponse {
  success: boolean;
  data?: CallOutcome;
}

/** What Iris makes of a call, for the outcome picker to open on. */
export interface CallOutcomeSuggestion {
  polarity: CallOutcomePolarity;
  /** Null when Iris has a read but cannot name a row in THIS tenant's list —
   *  the polarity still opens the picker on the right group. */
  outcomeId: string | null;
  outcomeLabel: string | null;
  /** One line in Iris's own reading, shown under the suggestion. */
  because: string;
  /** A note the agent can accept as-is, from the insight's own summary. */
  draftNote: string | null;
  /** What Iris heard us promise, as a one-line next step. Null when the call
   *  ended owing nothing — which is the answer on most resolved calls. */
  nextStep: string | null;
  /** How far off that next step is, in hours, or null when Iris heard no
   *  timing. The client turns it into an hour of the tenant's working day —
   *  a server-side instant would land at whatever o'clock the call ended. */
  dueInHours: number | null;
  /** Iris's own overview of the call. */
  summary: string | null;
  /**
   * What the reading is drawn from.
   *
   * `transcript` — she read the call. `history` — nobody spoke to this caller
   * (a missed call has no audio and never will), so this comes from what the
   * NUMBER has done before. Worded differently in the panel, because they
   * deserve different confidence.
   */
  basis: 'transcript' | 'history';
}

export interface CallOutcomeSuggestionResponse {
  success: boolean;
  /** Null is an ordinary answer, not an error: a voicemail row, or a call
   *  Iris has not read. The UI shows no chip. */
  data: CallOutcomeSuggestion | null;
}

/**
 * What one line of a call's history IS.
 *
 * `NOTE` is the only kind a person authors directly; the rest are written by
 * the server as the work happens. Before these existed, an assignment, a
 * callback attempt and a resolution all happened invisibly and the only
 * history a call had was what somebody happened to type.
 */
export type CallFollowUpEntryKind =
  | 'NOTE'
  | 'ATTEMPT'
  | 'ASSIGNED'
  | 'UNASSIGNED'
  | 'SCHEDULED'
  | 'SCHEDULE_CLEARED'
  | 'RESOLVED'
  | 'REOPENED'
  | 'CONTACT_LINKED';

/**
 * The structured half of a system entry.
 *
 * `body` is always a complete sentence on its own, so this is only ever used
 * to ADD to it — above all `at`, which has to be rendered in the reader's
 * timezone rather than in whichever one the server formatted in.
 */
export interface CallFollowUpEntryMeta {
  at?: string | null;
  action?: string | null;
  priority?: string | null;
  channel?: 'call' | 'sms' | null;
  outcomeLabel?: string | null;
  name?: string | null;
  contactId?: string | null;
}

/** One line of a call's history. */
export interface CallFollowUpEntry {
  id: string;
  kind: CallFollowUpEntryKind;
  body: string;
  meta: CallFollowUpEntryMeta | null;
  authorUserId: string | null;
  /** Null for an entry the system wrote — the backfill of the old single-note
   *  column, which cannot honestly credit anyone. */
  authorName: string | null;
  createdAt: string;
}

export interface CallFollowUpActivityResponse {
  success: boolean;
  data?: CallFollowUpEntry[];
}

export interface CallFollowUp {
  id: string;
  callControlId: string;
  reason: CallFollowUpReason;
  state: 'OPEN' | 'DONE';
  disposition: CallFollowUpDisposition | null;
  /** E.164, as the calling service sent it. */
  customerPhone: string;
  /**
   * The customer this CALL is linked to. Caller ID sets it when the call ends,
   * or somebody matches it afterwards. It is what opens a follow-up task and
   * what notes are mirrored onto — so it is a fact about the call, not about
   * the number.
   */
  contactId: string | null;
  /**
   * Who the NUMBER belongs to, when the call itself is linked to nobody.
   *
   * Resolved at read time against `contacts.phoneSearchKeys`, which is how a
   * customer saved AFTER their call gets named at all — the normal order for a
   * missed call is ring back, then save them. Lets the UI open that customer
   * and offer to link the call in one click. Null once `contactId` is set,
   * because then there is nothing left to resolve.
   */
  matchedContactId: string | null;
  assignedToUserId: string | null;
  leadId: string | null;
  /** When the call ENDED — what Open vs Cold is measured from. */
  callAt: string;
  /**
   * How many MORE times this caller rang while this follow-up was open.
   *
   * Their repeat calls are folded into this row: they are the same unmet need,
   * not new work, so the queue lists the caller once. 0 for almost every row.
   * Shown, never silent — a call that vanished from the queue with nothing to
   * explain it reads as a bug.
   */
  repeatCalls: number;
  /** The most recent of those repeat calls, or null if they only rang once. */
  lastCallAt: string | null;
  attemptCount: number;
  lastAttemptAt: string | null;
  /** DEPRECATED — superseded by the note history. Backfilled into it and no
   *  longer written; still sent so an old client keeps reading. */
  note: string | null;
  /** The tenant outcome this call was filed under, once somebody filed it. */
  outcomeId: string | null;
  /** Resolved by operations for the grid, so a cell needs no second fetch. */
  outcomeLabel: string | null;
  outcomePolarity: CallOutcomePolarity | null;
  /** The next step somebody committed to. Null is "nothing scheduled" — not
   *  "nothing to do", which an OPEN row already says. */
  followUpAt: string | null;
  followUpAction: string | null;
  /** The ContactTask a scheduled follow-up opened. Null for a caller nobody
   *  matched — a task needs a contact, and we never invent one off a call. */
  followUpTaskId: string | null;
  /** How urgent the next step is, in the task list's own vocabulary. Null
   *  means nobody chose — NOT `medium`, which is a choice. */
  followUpPriority: FollowUpPriority | null;
  /** Enough of the note history to tell a worked row from an untouched one
   *  without opening it. */
  noteCount: number;
  latestNote: string | null;
  latestNoteAt: string | null;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  /** Names resolved by operations for the page — null when the id is unset
   *  or no longer resolves. The UI never needs `users:view` to show an owner. */
  assigneeName: string | null;
  resolvedByName: string | null;
  contactName: string | null;
}

/**
 * How urgent a next step is.
 *
 * The `ContactTask` vocabulary, deliberately: scheduling a follow-up opens a
 * real task on the customer, and a priority this screen understood but the
 * task list did not would be a word nobody else could sort by.
 */
export type FollowUpPriority = 'low' | 'medium' | 'high' | 'urgent';

export const FOLLOW_UP_PRIORITIES = [
  'low',
  'medium',
  'high',
  'urgent',
] as const satisfies readonly FollowUpPriority[];

/**
 * The next step, as the client sends it.
 *
 * `at: null` drops it, and the server clears the rest with it. `assignedToUserId`
 * is three-valued on purpose: omitted leaves the owner alone, null returns the
 * call to the pool, an id assigns it.
 */
export interface FollowUpCommitmentInput {
  at: string | null;
  action?: string | null;
  assignedToUserId?: string | null;
  priority?: FollowUpPriority | null;
}

/** Everyone vs. just mine vs. nobody's yet. Omitted = everyone in scope. */
export type CallFollowUpOwnerFilter = 'me' | 'unassigned';

/** Someone a follow-up can be handed to. */
export interface FollowUpAssignee {
  id: string;
  name: string;
  email: string;
}

export interface FollowUpAssigneesResponse {
  success: boolean;
  data?: FollowUpAssignee[];
}

export interface CallFollowUpCounts {
  /** Every call still owed something, however old. */
  open: number;
  done: number;
}

export interface ListCallFollowUpsParams {
  tab?: CallFollowUpTab;
  reason?: CallFollowUpReason;
  assignedToUserId?: string;
  owner?: CallFollowUpOwnerFilter;
  /** Caller name or number. A number matches on its last ten digits, so any
   *  formatting works. */
  search?: string;
  /** ISO instants bounding the CALL time, built from whole days in the
   *  company timezone — the same way the call log builds its range. */
  startDate?: string;
  endDate?: string;
  outcomeId?: string;
  polarity?: CallOutcomePolarity;
  /** Closed rows nobody filed an outcome against. */
  unfiled?: boolean;
  page?: number;
  limit?: number;
}

/**
 * NOT `CallLogsPagination`. That one is the calling service's block and names
 * the current page `currentPage`; this queue is served by operations, which
 * names it `page`. Reusing the call-log type would declare a field the
 * response does not carry — the exact shape of the footer bug its own comment
 * records having been fixed.
 */
export interface CallFollowUpsPagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

export interface CallFollowUpsResponse {
  success: boolean;
  data?: {
    rows: CallFollowUp[];
    /** Unfiltered on purpose: a badge means work outstanding, not work matching
     *  whatever filter happens to be set. */
    counts: CallFollowUpCounts;
    pagination: CallFollowUpsPagination;
  } | null;
}

export interface CallFollowUpResponse {
  success: boolean;
  data?: CallFollowUp | null;
}
